import { useState } from 'react';
import { createGpsCustomMetric } from '../../api';
import { METRIC_CATALOG } from './metricsCatalog';

const OPERATORS = [
  { value: 'divide',   label: '÷' },
  { value: 'multiply', label: '×' },
  { value: 'add',      label: '+' },
  { value: 'subtract', label: '−' },
];

// Only numeric base metrics are valid formula operands — the HR-zone-time columns hold raw
// "mm:ss" strings, not numbers (see METRIC_CATALOG's isTime flag).
const OPERAND_METRICS = METRIC_CATALOG.filter(m => !m.isTime);

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 };
const fieldStyle = { width: '100%', padding: 9, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, boxSizing: 'border-box' };

// Deliberately a single two-operand formula (Metric A <op> Metric B, B optionally a constant)
// rather than a full expression language — matches exactly what was asked for (e.g. "Avg Sprint
// Distance" = Distance Zone 6 ÷ Entries Zone 6). No eval()/expression parser: the definition is
// just {metric_a, op, metric_b_type, metric_b}, evaluated per-row client-side by
// MetricsTable.js's evalCustomMetric.
export default function CustomMetricModal({ user, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [metricA, setMetricA] = useState(OPERAND_METRICS[0]?.key || '');
  const [op, setOp] = useState('divide');
  const [metricBType, setMetricBType] = useState('metric');
  const [metricB, setMetricB] = useState(OPERAND_METRICS[1]?.key || '');
  const [constantB, setConstantB] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const bValue = metricBType === 'metric' ? metricB : constantB;
  const canSave = name.trim() && metricA && bValue !== '' && bValue !== null && bValue !== undefined;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const created = await createGpsCustomMetric({
        name: name.trim(),
        unit: unit.trim() || null,
        metric_a: metricA,
        op,
        metric_b_type: metricBType,
        metric_b: metricBType === 'constant' ? constantB : metricB,
        created_by: user?.id || null,
      });
      onCreated(created);
    } catch (err) {
      console.error('Failed to save custom metric:', err);
      setError('Failed to save — try again.');
    } finally {
      setSaving(false);
    }
  };

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const boxStyle = { background: 'var(--bg-primary, #fff)', color: 'var(--text-primary, #111)', borderRadius: 12, padding: 24, width: 460, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={boxStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>New calculated metric</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'inherit' }}>×</button>
        </div>

        {error && <div style={{ color: '#ef4444', marginBottom: 12, fontSize: 13 }}>{error}</div>}

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Avg Sprint Distance" style={fieldStyle} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Unit (optional)</label>
          <input value={unit} onChange={e => setUnit(e.target.value)} placeholder="e.g. m" style={fieldStyle} />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Metric A</label>
          <select value={metricA} onChange={e => setMetricA(e.target.value)} style={fieldStyle}>
            {OPERAND_METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Operator</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {OPERATORS.map(o => (
              <button key={o.value} onClick={() => setOp(o.value)}
                style={{ flex: 1, padding: 9, borderRadius: 8, border: `1px solid ${op === o.value ? '#3b82f6' : 'var(--border-strong)'}`, background: op === o.value ? '#3b82f6' : 'var(--bg-app)', color: op === o.value ? '#fff' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 15, fontWeight: 700 }}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Metric B</label>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
            <button onClick={() => setMetricBType('metric')}
              style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: `1px solid ${metricBType === 'metric' ? '#3b82f6' : 'var(--border-strong)'}`, background: metricBType === 'metric' ? 'rgba(59,130,246,0.12)' : 'var(--bg-app)', color: metricBType === 'metric' ? '#3b82f6' : 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Existing metric
            </button>
            <button onClick={() => setMetricBType('constant')}
              style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: `1px solid ${metricBType === 'constant' ? '#3b82f6' : 'var(--border-strong)'}`, background: metricBType === 'constant' ? 'rgba(59,130,246,0.12)' : 'var(--bg-app)', color: metricBType === 'constant' ? '#3b82f6' : 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Constant number
            </button>
          </div>
          {metricBType === 'metric' ? (
            <select value={metricB} onChange={e => setMetricB(e.target.value)} style={fieldStyle}>
              {OPERAND_METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
          ) : (
            <input type="number" value={constantB} onChange={e => setConstantB(e.target.value)} placeholder="e.g. 90" style={fieldStyle} />
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          <button onClick={handleSave} disabled={!canSave || saving}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: canSave ? '#3b82f6' : 'var(--border)', color: canSave ? '#fff' : 'var(--text-dimmed)', cursor: canSave ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600 }}>
            {saving ? 'Saving…' : 'Save metric'}
          </button>
        </div>
      </div>
    </div>
  );
}
